import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CvShareLink, CvTargetKey } from '../../lib/cv-collaboration-api';
import { buildCvShareUrl } from '../../lib/cv-share-url';
import {
  useCvPrivateLink,
  useEnsureCvPrivateLink,
  useUpdateCvShareLink,
} from '../../hooks/useCvCollaborationApi';
import type { CvVisibilityOptions } from './CVSharePanel';
import { shouldAutomaticallyPreparePrivateShareLink } from './cv-share-preparation';
import {
  getPublicHandleFromHostname,
  isPublicHandleLike,
  normalizePublicHandle,
} from '@tabbio/utils/public-url';

function visibilityPayload(visibility: CvVisibilityOptions): Record<string, unknown> {
  return {
    showEmail: visibility.showEmail,
    showPhone: visibility.showPhone,
    showLocation: visibility.showLocation,
    showLinks: visibility.showLinks,
    showPhoto: visibility.showPhoto,
  };
}

/**
 * The same key `visibilityKey` produces, from a link record the API returned,
 * so a reused link's stored visibility can be compared with the toggle.
 */
function visibilityKeyFromRecord(
  visibility: Record<string, unknown> | null | undefined,
): string {
  return JSON.stringify({
    showEmail: Boolean(visibility?.showEmail),
    showPhone: Boolean(visibility?.showPhone),
    showLocation: Boolean(visibility?.showLocation),
    showLinks: Boolean(visibility?.showLinks),
    showPhoto: Boolean(visibility?.showPhoto),
  });
}

/** A stored link counts as the contact link only if it already serves both. */
function sharesContact(visibility: Record<string, unknown> | null | undefined) {
  return Boolean(visibility?.showEmail) && Boolean(visibility?.showPhone);
}

export function useCompactCvShareLink({
  target,
  publicUrl,
  visibility,
  requiresPrivateLink,
  adoptExistingContactLink = false,
}: {
  target: CvTargetKey;
  publicUrl?: string | null;
  visibility: CvVisibilityOptions;
  requiresPrivateLink: boolean;
  /**
   * Show the standing link the owner already made (this session, an earlier
   * one, or another device) instead of a "Create link" prompt. Read-only: it
   * never issues a link. Only a link already sharing phone & email is
   * adopted, so opening the sheet can never widen what a live link exposes.
   */
  adoptExistingContactLink?: boolean;
}) {
  const {
    mutateAsync: ensurePrivateLink,
    isPending: isCreatingShareLink,
  } = useEnsureCvPrivateLink(target);
  const {
    mutateAsync: updateShareLink,
    isPending: isUpdatingShareLink,
  } = useUpdateCvShareLink(target);
  const [privateUrl, setPrivateUrl] = useState<string | null>(null);
  const [privateLinkId, setPrivateLinkId] = useState<string | null>(null);
  // The live link's record, for the link details (audit E03): what it shares
  // and under which policy. Kept in step with every create, adopt, patch and
  // revoke below, so it never describes a link that is not the one on screen.
  const [privateLink, setPrivateLink] = useState<CvShareLink | null>(null);
  const [privateUrlError, setPrivateUrlError] = useState(false);
  const [visibilitySyncError, setVisibilitySyncError] = useState(false);
  // True once the owner revoked the link in this session, until a new one is
  // made. Drives the "Link revoked" confirmation in the contact-link sheet.
  const [revoked, setRevoked] = useState(false);
  const lastAutomaticAttemptKeyRef = useRef<string | null>(null);
  // Visibility the live link was last written with. Visibility is a property of
  // the link, not part of its identity, so a change is PATCHed onto the existing
  // record. Minting a new token instead would leave the previous one live and
  // still serving the old visibility — turning contacts off would not actually
  // hide them from anyone already holding the link.
  const syncedVisibilityKeyRef = useRef<string | null>(null);
  const visibilityKey = useMemo(
    () => JSON.stringify(visibilityPayload(visibility)),
    [visibility],
  );
  const ownerHandle = useMemo(
    () => inferOwnerHandleFromPublicUrl(publicUrl),
    [publicUrl],
  );
  const { data: existingLink, isFetching: isFetchingExistingLink } =
    useCvPrivateLink(target, { enabled: adoptExistingContactLink });

  // Only the target invalidates the cached link. Visibility deliberately stays
  // out of this key: the contact toggle must not rotate the token.
  useEffect(() => {
    setPrivateUrl(null);
    setPrivateLinkId(null);
    setPrivateLink(null);
    setPrivateUrlError(false);
    setVisibilitySyncError(false);
    setRevoked(false);
    lastAutomaticAttemptKeyRef.current = null;
    syncedVisibilityKeyRef.current = null;
  }, [target]);

  const ensureShareUrl = useCallback(
    async (options?: { forcePrivate?: boolean; revokeExisting?: boolean }) => {
      const shouldCreatePrivate = options?.forcePrivate || requiresPrivateLink;
      if (!shouldCreatePrivate) {
        return publicUrl ?? null;
      }

      if (privateUrl && !options?.forcePrivate) {
        return privateUrl;
      }

      setPrivateUrlError(false);
      try {
        const result = await ensurePrivateLink({
          permissions: ['view'],
          visibility: visibilityPayload(visibility),
          allowDownload: false,
          rotate: Boolean(options?.revokeExisting),
        });
        const nextUrl = buildCvShareUrl(result.token, result.shareUrl, ownerHandle);
        setPrivateUrl(nextUrl);
        setPrivateLinkId(result.shareLink?.id ?? null);
        setPrivateLink(result.shareLink ?? null);
        // A link that already existed carries whatever visibility it was left
        // with, which need not match the toggle. Seed the key from the record
        // so the sync effect patches it when they disagree — and never treat a
        // disagreement as a reason to rotate.
        syncedVisibilityKeyRef.current = visibilityKeyFromRecord(
          result.shareLink?.visibility,
        );
        setVisibilitySyncError(false);
        setRevoked(false);
        return nextUrl;
      } catch (error) {
        // A failed regeneration must not hide the last working link. Only the
        // first-time creation path needs the inline Retry state.
        setPrivateUrlError(!privateUrl);
        throw error;
      }
    },
    [
      ensurePrivateLink,
      ownerHandle,
      privateUrl,
      publicUrl,
      requiresPrivateLink,
      visibility,
    ],
  );

  const syncPrivateVisibility = useCallback(
    async (shareLinkId: string, attemptedKey: string) => {
      // Recorded before the request so a failure waits for the next change or an
      // explicit retry instead of re-firing from the effect on every render.
      syncedVisibilityKeyRef.current = attemptedKey;
      setVisibilitySyncError(false);
      const sent = visibilityPayload(visibility);
      try {
        const updated = await updateShareLink({
          shareLinkId,
          visibility: sent,
        });
        // The link now serves what was sent. Prefer the server's record, but
        // never let a partial answer leave the old visibility on screen.
        setPrivateLink((current) =>
          current && current.id === shareLinkId
            ? {
                ...current,
                ...(updated && typeof updated === 'object' ? updated : {}),
                visibility:
                  updated && typeof updated === 'object' && updated.visibility
                    ? updated.visibility
                    : sent,
              }
            : current,
        );
      } catch (error) {
        // The token stays valid, but it still serves the previous visibility.
        // Surfaced inline so the switch is not silently lying about what the
        // link exposes.
        setVisibilitySyncError(true);
        throw error;
      }
    },
    [updateShareLink, visibility],
  );

  useEffect(() => {
    if (!privateLinkId) {
      // A live link with no id cannot be patched, so a visibility change would
      // silently not apply. Say so rather than let the switch imply it did.
      if (privateUrl && syncedVisibilityKeyRef.current !== visibilityKey) {
        setVisibilitySyncError(true);
      }
      return;
    }
    if (syncedVisibilityKeyRef.current === visibilityKey) return;
    // One patch at a time. Flipping the switch again mid-request is not lost:
    // this re-runs when the in-flight write settles and reconciles against
    // whatever the visibility is by then, so the last state always wins.
    if (isUpdatingShareLink) return;
    void syncPrivateVisibility(privateLinkId, visibilityKey).catch(() => {});
  }, [
    isUpdatingShareLink,
    privateLinkId,
    privateUrl,
    syncPrivateVisibility,
    visibilityKey,
  ]);

  useEffect(() => {
    if (!adoptExistingContactLink || !existingLink?.token) return;
    // Wait for the fetch this mount started: cached data can still be a link
    // that was revoked after it was read.
    if (isFetchingExistingLink) return;
    // Something newer already happened in this session.
    if (privateUrl || revoked || isCreatingShareLink) return;
    if (!sharesContact(existingLink.shareLink?.visibility)) return;

    setPrivateUrl(
      buildCvShareUrl(existingLink.token, existingLink.shareUrl, ownerHandle),
    );
    setPrivateLinkId(existingLink.shareLink?.id ?? null);
    setPrivateLink(existingLink.shareLink ?? null);
    // Seeded from the record, so the sync effect reconciles any other field
    // that drifted, exactly as it does after Create.
    syncedVisibilityKeyRef.current = visibilityKeyFromRecord(
      existingLink.shareLink?.visibility,
    );
  }, [
    adoptExistingContactLink,
    existingLink,
    isCreatingShareLink,
    isFetchingExistingLink,
    ownerHandle,
    privateUrl,
    revoked,
  ]);

  // Revoking retires the standing link server-side (revokedAt set, token
  // nulled), so the next ensure issues a fresh token rather than reviving it.
  const revokePrivateUrl = useCallback(async () => {
    if (!privateLinkId) {
      throw new Error('No private link to revoke');
    }
    await updateShareLink({ shareLinkId: privateLinkId, revoked: true });
    setPrivateUrl(null);
    setPrivateLinkId(null);
    setPrivateLink(null);
    setPrivateUrlError(false);
    setVisibilitySyncError(false);
    setRevoked(true);
    syncedVisibilityKeyRef.current = null;
  }, [privateLinkId, updateShareLink]);

  useEffect(() => {
    if (!requiresPrivateLink) {
      // The private link is not on screen. Keep the token so re-enabling shows
      // the same URL, and allow one fresh automatic attempt if it never got one.
      lastAutomaticAttemptKeyRef.current = null;
      return;
    }

    if (
      !shouldAutomaticallyPreparePrivateShareLink({
        requiresPrivateLink,
        privateUrl,
        isPending: isCreatingShareLink,
        attemptKey: target,
        lastAttemptKey: lastAutomaticAttemptKeyRef.current,
      })
    ) {
      return;
    }

    // Record the attempt before starting it. A failed request must wait for an
    // explicit Retry action instead of re-running on every mutation render.
    lastAutomaticAttemptKeyRef.current = target;
    void ensureShareUrl({ forcePrivate: true }).catch(() => {});
  }, [
    ensureShareUrl,
    isCreatingShareLink,
    privateUrl,
    requiresPrivateLink,
    target,
  ]);

  return {
    publicUrl: publicUrl ?? null,
    privateUrl,
    privateLink,
    privateUrlError,
    visibilitySyncError,
    revoked,
    activeUrl: requiresPrivateLink ? privateUrl : publicUrl ?? null,
    isPreparingPrivateUrl: requiresPrivateLink && !privateUrl && isCreatingShareLink,
    isCreatingPrivateUrl: isCreatingShareLink,
    // While the read is in flight the sheet cannot tell "no link yet" from
    // "not loaded yet", so it shows a spinner instead of a Create prompt.
    isLoadingExistingLink:
      adoptExistingContactLink &&
      isFetchingExistingLink &&
      !privateUrl &&
      !revoked,
    isSyncingPrivateVisibility: isUpdatingShareLink,
    ensureShareUrl,
    retryPrivateUrl: () => ensureShareUrl({ forcePrivate: true }),
    // With no id there is nothing to patch, so the only way to apply the change
    // is a fresh link — revoking the stale one rather than leaving it live on
    // the previous visibility.
    retryPrivateVisibility: () =>
      privateLinkId
        ? syncPrivateVisibility(privateLinkId, visibilityKey)
        : ensureShareUrl({ forcePrivate: true, revokeExisting: true }).then(
            () => undefined,
          ),
    regeneratePrivateUrl: () =>
      ensureShareUrl({ forcePrivate: true, revokeExisting: true }),
    createPrivateUrl: () => ensureShareUrl({ forcePrivate: true }),
    revokePrivateUrl,
  };
}

function inferOwnerHandleFromPublicUrl(publicUrl: string | null | undefined) {
  if (!publicUrl) return null;
  try {
    const parsed = new URL(
      publicUrl.startsWith('http://') || publicUrl.startsWith('https://')
        ? publicUrl
        : `https://${publicUrl}`,
    );
    const subdomainHandle = getPublicHandleFromHostname(parsed.hostname);
    if (subdomainHandle) return subdomainHandle;

    const firstPathSegment = parsed.pathname.split('/').filter(Boolean)[0];
    return isPublicHandleLike(firstPathSegment)
      ? normalizePublicHandle(firstPathSegment)
      : null;
  } catch {
    return null;
  }
}
